import { ice } from '@halvard/rules';

/**
 * Where a warrior is during an animation, in board terms: square (fractional while moving),
 * ice level under it (`WATER` once off the ice), hop height, how far it has gone under
 * (0 = standing, 1 = gone), which way it faces (`yaw`, radians; 0 faces rank 1), how far it
 * leans forward (radians; negative leans back) and how squashed it is (1 = normal height).
 */
export interface Pose {
  r: number;
  c: number;
  level: number;
  lift: number;
  sink: number;
  yaw: number;
  lean: number;
  squash: number;
}

export interface Keyframe extends Pose {
  t: number;
}

/** A short burst drawn on the ice: snow kicked up on landing, or a splash going under. */
export interface Effect {
  t: number;
  kind: 'puff' | 'splash';
  r: number;
  c: number;
  level: number;
  /** About 1 for a normal landing; bigger for a Huscarl's stomp. */
  size: number;
}

export interface Timeline {
  /** Seconds until every warrior is at rest. */
  duration: number;
  /** Keyframes per warrior id, in time order. Warriors without a track don't move. */
  tracks: Map<string, Keyframe[]>;
  effects: Effect[];
}

/** Level drawn for open water: below thin ice. */
export const WATER = -1;
export const SLIDE_TIME = 0.3;
export const SINK_TIME = 0.7;
export const DROP_TIME = 0.35;
/** Time to turn back to face the enemy after moving. */
export const SETTLE_TIME = 0.25;
export const EFFECT_TIME = { puff: 0.45, splash: 0.8 } as const;

/**
 * How each warrior moves, one step at a time. The Skier glides low and fast, the Spear trots
 * in light hops, the Huscarl stomps: slow, low and heavy, landing with a thud.
 */
export const GAITS: Readonly<
  Record<
    ice.WarriorType,
    { time: number; lift: number; lean: number; squash: number; dust: number }
  >
> = {
  skier: { time: 0.18, lift: 0.04, lean: 0.28, squash: 0.95, dust: 0.5 },
  spear: { time: 0.22, lift: 0.22, lean: 0.14, squash: 0.86, dust: 0.75 },
  huscarl: { time: 0.3, lift: 0.12, lean: 0.08, squash: 0.74, dust: 1.2 },
};
/** Steps up or down take longer and hop higher, so warriors clear the edge of the slab. */
export const CLIMB = { time: 0.06, lift: 0.3 } as const;

/**
 * What happens to a warrior in a shoved line: it `lands`, or it is lost `off` the lake, into a
 * `hole`, by a `fall` (the square is 2+ levels lower: it tumbles off the ledge and breaks through)
 * or by a `crush` (2+ levels higher: it is slammed against the wall of ice and never climbs it).
 */
export type SlideFate = 'lands' | 'off' | 'hole' | 'fall' | 'crush';

export function slideFate(heights: number[][], slide: ice.Slide): SlideFate {
  if (!slide.sunk) return 'lands';
  const { to, from } = slide;
  if (to.r < 0 || to.r > 8 || to.c < 0 || to.c > 8) return 'off';
  const landing = heights[to.r]?.[to.c];
  if (landing === undefined || landing === ice.HOLE) return 'hole';
  return landing < (heights[from.r]?.[from.c] ?? 0) ? 'fall' : 'crush';
}

/** Time to tumble off a ledge, and to be flattened against a wall. */
export const FALL_TIME = 0.32;
export const CRUSH_TIME = 0.14;

/** The way a side faces at rest: toward the enemy. */
export function restYaw(side: ice.Side): number {
  return side === 'brand' ? Math.PI : 0;
}

/** The yaw that faces a step of (dr, dc) on the board. */
export function yawToward(dr: number, dc: number): number {
  return Math.atan2(dc, dr);
}

function levelAt(heights: number[][], r: number, c: number): number {
  const h = heights[r]?.[c];
  return h === undefined || h === ice.HOLE ? WATER : h;
}

/**
 * Keyframes and effects for the events of one action, played from the state before it:
 * placing (dropped in), walking (one gait step per square, turning to face the way), shoving
 * (wind-up, lunge, and the line knocked back on contact), going under, and the thaw.
 */
export function buildTimeline(before: ice.IceState, events: readonly ice.IceEvent[]): Timeline {
  const tracks = new Map<string, Keyframe[]>();
  const effects: Effect[] = [];
  const heights = before.heights;
  const pose = new Map<string, Pose>();
  const kinds = new Map<string, ice.Warrior>();
  for (const w of before.warriors) {
    kinds.set(w.id, w);
    pose.set(w.id, {
      r: w.r,
      c: w.c,
      level: levelAt(heights, w.r, w.c),
      lift: 0,
      sink: 0,
      yaw: restYaw(w.side),
      lean: 0,
      squash: 1,
    });
  }
  let clock = 0;

  const key = (id: string, t: number, next: Partial<Pose>) => {
    const prev = pose.get(id);
    if (!prev) return;
    const full = { ...prev, ...next };
    const track = tracks.get(id) ?? [];
    // A warrior's first keyframe starts it from where it stood.
    if (track.length === 0 && t > 0) track.push({ t: 0, ...prev });
    track.push({ t, ...full });
    tracks.set(id, track);
    pose.set(id, full);
  };
  const hold = (id: string, t: number) => key(id, t, {});
  const effect = (e: Effect) => effects.push(e);

  for (const event of events) {
    switch (event.kind) {
      case 'placed': {
        const w = event.warrior;
        kinds.set(w.id, w);
        const level = levelAt(heights, w.r, w.c);
        const base = { r: w.r, c: w.c, level, sink: 0, yaw: restYaw(w.side), lean: 0 };
        tracks.set(w.id, [
          { t: 0, ...base, lift: 1.2, squash: 1 },
          { t: DROP_TIME, ...base, lift: 0, squash: 0.8 },
          { t: DROP_TIME + 0.12, ...base, lift: 0, squash: 1 },
        ]);
        pose.set(w.id, { ...base, lift: 0, squash: 1 });
        effect({ t: DROP_TIME, kind: 'puff', r: w.r, c: w.c, level, size: 0.8 });
        clock = DROP_TIME + 0.12;
        break;
      }
      case 'moved': {
        const w = kinds.get(event.id);
        const gait = GAITS[w?.type ?? 'spear'];
        hold(event.id, clock);
        for (const step of event.path.slice(1)) {
          const from = pose.get(event.id);
          if (!from) break;
          const level = levelAt(heights, step.r, step.c);
          const climb = Math.abs(level - from.level);
          const time = gait.time + CLIMB.time * climb;
          const yaw = yawToward(step.r - from.r, step.c - from.c);
          const t0 = clock;
          // Turn first, then take off, land squashed, and spring back up.
          key(event.id, t0 + time * 0.2, { yaw, lean: gait.lean * 0.5 });
          key(event.id, t0 + time * 0.55, {
            r: (from.r + step.r) / 2,
            c: (from.c + step.c) / 2,
            level: Math.max(from.level, level),
            lift: gait.lift + CLIMB.lift * Math.max(0, level - from.level) + 0.04 * climb,
            lean: gait.lean,
            squash: 1.04,
          });
          key(event.id, t0 + time * 0.85, {
            r: step.r,
            c: step.c,
            level,
            lift: 0,
            squash: gait.squash,
          });
          key(event.id, t0 + time, { squash: 1, lean: gait.lean * 0.5 });
          effect({
            t: t0 + time * 0.85,
            kind: 'puff',
            r: step.r,
            c: step.c,
            level,
            size: gait.dust,
          });
          clock += time;
        }
        if (w) key(event.id, clock + SETTLE_TIME, { yaw: restYaw(w.side), lean: 0 });
        break;
      }
      case 'shoved': {
        const shover = pose.get(event.id);
        const first = event.slides.at(-1);
        if (!shover || !first) break;
        const w = kinds.get(event.id);
        const yaw = yawToward(event.direction.dr, event.direction.dc);
        hold(event.id, clock);
        // Wind up: turn to the enemy and lean back.
        key(event.id, clock + 0.1, { yaw, lean: -0.25, squash: 1.05 });
        // Lunge into the enemy: the line is knocked back at contact.
        const contact = clock + 0.22;
        key(event.id, contact, {
          r: (shover.r + first.from.r) / 2,
          c: (shover.c + first.from.c) / 2,
          level: Math.max(shover.level, levelAt(heights, first.from.r, first.from.c)),
          lift: 0.06,
          lean: 0.45,
          squash: 0.95,
        });
        const landing = levelAt(heights, first.from.r, first.from.c);
        effect({
          t: contact,
          kind: 'puff',
          r: first.from.r,
          c: first.from.c,
          level: landing,
          size: 1,
        });
        let end = contact + SLIDE_TIME;
        for (const slide of event.slides) {
          hold(slide.id, contact);
          const from = levelAt(heights, slide.from.r, slide.from.c);
          const to = levelAt(heights, slide.to.r, slide.to.c);
          const fate = slideFate(heights, slide);
          // The edge between the two squares, still on the warrior's own level.
          const edge = {
            r: (slide.from.r + slide.to.r) / 2,
            c: (slide.from.c + slide.to.c) / 2,
            level: from,
          };
          key(slide.id, contact + 0.06, { lean: -0.35, squash: 0.9 });
          if (fate === 'lands') {
            key(slide.id, contact + SLIDE_TIME, {
              r: slide.to.r,
              c: slide.to.c,
              level: to,
              lift: 0,
              lean: -0.1,
              squash: 1,
            });
            key(slide.id, contact + SLIDE_TIME + 0.15, { lean: 0 });
            continue;
          }
          const atEdge = contact + SLIDE_TIME * 0.6;
          if (fate === 'crush') {
            // Slammed into the wall of ice: flattened against it, then shattered.
            key(slide.id, atEdge, { ...edge, lift: 0, lean: -0.15, squash: 1 });
            key(slide.id, atEdge + CRUSH_TIME, { squash: 0.22, lean: 0 });
            effect({ t: atEdge, kind: 'puff', ...edge, size: 1.5 });
            key(slide.id, atEdge + CRUSH_TIME + 0.2, { squash: 0.05, sink: 1 });
            end = Math.max(end, atEdge + CRUSH_TIME + 0.2);
            continue;
          }
          // Pushed to the edge, then over it: a tumble down to the landing (water when off the
          // lake or into a hole), a splash, and gone.
          const bottom = fate === 'fall' ? to : WATER;
          key(slide.id, atEdge, { ...edge, lift: 0, lean: -0.4, squash: 1 });
          const landed = atEdge + FALL_TIME + 0.04 * (from - bottom);
          key(slide.id, landed, {
            r: slide.to.r,
            c: slide.to.c,
            level: bottom,
            lift: 0,
            lean: -1.4,
          });
          effect({
            t: landed,
            kind: 'splash',
            r: slide.to.r,
            c: slide.to.c,
            level: bottom,
            size: 1,
          });
          key(slide.id, landed + SINK_TIME, { sink: 1, lean: -1.6 });
          end = Math.max(end, landed + SINK_TIME);
        }
        key(event.id, contact + SLIDE_TIME, {
          r: first.from.r,
          c: first.from.c,
          level: landing,
          lift: 0,
          lean: 0.1,
          squash: 1,
        });
        if (w) key(event.id, contact + SLIDE_TIME + SETTLE_TIME, { yaw: restYaw(w.side), lean: 0 });
        clock = end;
        break;
      }
      case 'thawed': {
        if (event.sunk === null) break;
        const at = pose.get(event.sunk);
        if (!at) break;
        hold(event.sunk, clock);
        effect({ t: clock, kind: 'splash', r: event.r, c: event.c, level: WATER, size: 1 });
        key(event.sunk, clock + SINK_TIME, { level: WATER, sink: 1 });
        clock += SINK_TIME;
        break;
      }
      default:
        break;
    }
  }
  const settled = Math.max(clock, ...[...tracks.values()].map((track) => track.at(-1)?.t ?? 0));
  return { duration: settled, tracks, effects };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Turns from one angle to another the short way round. */
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/** The pose on a track at time `t`, eased between keyframes. */
export function poseAt(track: readonly Keyframe[], t: number): Pose | null {
  const first = track[0];
  if (!first) return null;
  if (t <= first.t) return first;
  for (let i = 1; i < track.length; i++) {
    const a = track[i - 1];
    const b = track[i];
    if (!a || !b || t > b.t) continue;
    const span = b.t - a.t;
    const k = span <= 0 ? 1 : (t - a.t) / span;
    const eased = k * k * (3 - 2 * k);
    return {
      r: lerp(a.r, b.r, eased),
      c: lerp(a.c, b.c, eased),
      level: lerp(a.level, b.level, eased),
      lift: lerp(a.lift, b.lift, eased),
      sink: lerp(a.sink, b.sink, k),
      yaw: lerpAngle(a.yaw, b.yaw, eased),
      lean: lerp(a.lean, b.lean, eased),
      squash: lerp(a.squash, b.squash, eased),
    };
  }
  return track.at(-1) ?? null;
}

/** How far through an effect is at time `t`: 0–1 while showing, null before or after. */
export function effectProgress(effect: Effect, t: number): number | null {
  const k = (t - effect.t) / EFFECT_TIME[effect.kind];
  return k >= 0 && k <= 1 ? k : null;
}
